/* Spazioil size guidance: room labels and one larger editorial recommendation.
   Native option values, variant links and customer frame choices stay intact. */
(function () {
  'use strict';
  const FAMILIES = [
    { ratio: 1, anchor: [48, 48], pick: '55x55' },
    { ratio: 3 / 4, anchor: [40, 54], pick: '48x64' },
    { ratio: 2 / 3, anchor: [40, 60], pick: '48x72' },
    { ratio: 1 / 2, anchor: [36, 72], pick: '40x80' },
    { ratio: 4 / 3, anchor: [54, 40], pick: '64x48' },
    { ratio: 3 / 2, anchor: [60, 40], pick: '72x48' },
    { ratio: 2, anchor: [72, 36], pick: '80x40' },
    { ratio: 3, anchor: [72, 24], pick: '90x30' }
  ];
  const PORTRAIT_LABELS = {
    '24x32': 'Nook / bedroom corner', '24x36': 'Powder room / entryway',
    '30x40': 'Above a nightstand', '32x48': 'Medium bedroom wall',
    '36x48': 'Living room accent', '36x54': 'Medium feature wall',
    '40x54': 'Standard living room', '40x60': 'Tall living room wall',
    '48x64': 'Statement wall', '48x72': 'Grand living room',
    '53x80': 'Oversized feature wall', '54x72': 'Luxury primary suite',
    '60x80': 'Grand statement piece', '75x100': 'Gallery scale'
  };
  function parseSize(value) {
    const match = String(value || '').replace(/[“”″]/g, '"').replace(/[‘’′]/g, "'")
      .replace(/\s+/g, '').match(/(\d+)["']*[X×](\d+)["']*\/(\d+)[X×](\d+)CM/i);
    if (!match) return null;
    const w = Number(match[1]), h = Number(match[2]);
    return { key: `${w}x${h}`, w, h, area: w * h, ratio: w / h,
      compact: `${w}×${h}″ / ${match[3]}×${match[4]} cm` };
  }
  function familyFor(sizes) {
    return FAMILIES.map(family => ({ family, count: sizes.filter(size =>
      Math.abs(Math.log(size.ratio / family.ratio)) < 0.065).length }))
      .sort((a, b) => b.count - a.count)[0]?.family;
  }
  function recommendation(sizes, family) {
    if (!family) return null;
    const exact = sizes.find(size => size.key === family.pick);
    if (exact) return exact.key;
    const anchorArea = family.anchor[0] * family.anchor[1];
    return sizes.filter(size => size.area > anchorArea && Math.abs(Math.log(size.ratio / family.ratio)) < 0.09)
      .sort((a, b) => a.area - b.area)[0]?.key || null;
  }
  function roomLabel(size) {
    if (PORTRAIT_LABELS[size.key]) return PORTRAIT_LABELS[size.key];
    const longest = Math.max(size.w, size.h);
    if (Math.abs(size.ratio - 1) < 0.07) {
      if (longest <= 24) return 'Nook / entryway';
      if (longest <= 36) return 'Bedroom accent';
      if (longest <= 44) return 'Above a console';
      if (longest <= 48) return 'Living room focal point';
      if (longest <= 60) return 'Statement wall';
      if (longest <= 80) return 'Oversized feature wall';
      return 'Gallery scale';
    }
    if (size.w > size.h) {
      if (longest <= 40) return 'Entryway / console';
      if (longest <= 54) return 'Above a bed';
      if (longest <= 60) return 'Above a sofa';
      if (longest <= 72) return 'Living room statement';
      if (longest <= 80) return 'Wide feature wall';
      if (longest <= 90) return 'Open-plan living';
      return 'Gallery scale';
    }
    if (longest <= 40) return 'Entryway / bedroom';
    if (longest <= 54) return 'Bedroom feature wall';
    if (longest <= 64) return 'Living room statement';
    if (longest <= 80) return 'Tall feature wall';
    return 'Gallery scale';
  }
  function selectDefault(select, pick) {
    if (select.dataset.spazioilDefaultSelectionChecked === 'true') return;
    select.dataset.spazioilDefaultSelectionChecked = 'true';
    if (!pick || new URLSearchParams(location.search).has('variant')) return;
    const picker = select.closest('#MainContent [id^="MainProduct-"] variant-selects');
    if (!picker || !location.pathname.includes('/products/')) return;
    const selects = Array.from(picker.querySelectorAll('select'));
    const index = selects.indexOf(select);
    const data = picker.querySelector('script[type="application/json"]');
    if (index < 0 || !data) return;
    let variants;
    try { variants = JSON.parse(data.textContent); } catch (_) { return; }
    if (!Array.isArray(variants)) return;
    // Never substitute a different frame or a sold-out combination to force the recommendation.
    const variant = variants.find(variant => variant.available && parseSize(variant.options[index])?.key === pick &&
      variant.options.every((value, i) => i === index || value === selects[i]?.value));
    if (!variant || select.value === variant.options[index]) return;
    select.value = variant.options[index];
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
  function updateNote(select) {
    const size = parseSize(select.value);
    if (!size) return;
    let note = select._spazioilGuidanceNote;
    if (!note?.isConnected) {
      note = document.createElement('div');
      note.className = 'spazioil-size-note';
      note.setAttribute('aria-live', 'polite');
      (select.closest('.select') || select.parentElement).insertAdjacentElement('afterend', note);
      select._spazioilGuidanceNote = note;
    }
    const picked = size.key === select.dataset.spazioilRecommendedSize;
    note.replaceChildren();
    const title = document.createElement('strong');
    title.className = 'spazioil-size-note__label';
    title.textContent = `${picked ? 'Editor’s Pick · ' : ''}${roomLabel(size)}`;
    const copy = document.createElement('span');
    copy.textContent = picked ? 'Bring the texture into focus at a larger scale. Check your wall with the Size Guide.' :
      'A placement suggestion for this size. Use the Size Guide to check the fit on your wall.';
    note.append(title, copy);
  }
  function decorate(select) {
    if (!select.closest('variant-selects')) return;
    const sizes = Array.from(select.options).map(option => parseSize(option.value)).filter(Boolean);
    if (sizes.length < 3) return;
    const family = familyFor(sizes);
    const pick = recommendation(sizes, family);
    select.dataset.spazioilSizeSelect = 'true';
    const field = select.closest('fieldset');
    if (field) field.dataset.spazioilSizeField = 'true';
    select.dataset.spazioilRecommendedSize = pick || '';
    for (const option of select.options) {
      const size = parseSize(option.value);
      if (!size) continue;
      const caption = size.key === pick ? 'Editor’s Pick' : roomLabel(size);
      const availability = option.disabled ? ' · Unavailable' : '';
      const text = `${size.compact} · ${caption}${availability}`;
      if (option.textContent !== text) option.textContent = text;
    }
    selectDefault(select, pick);
    updateNote(select);
    if (select.dataset.spazioilSizeListener !== 'true') {
      select.addEventListener('change', () => updateNote(select));
      select.dataset.spazioilSizeListener = 'true';
    }
  }
  function scan(root) {
    if (root.matches?.('select')) decorate(root);
    root.querySelectorAll('select').forEach(decorate);
  }
  function initialize() {
    if (!document.getElementById('spazioil-size-guidance-style')) {
      const style = document.createElement('style');
      style.id = 'spazioil-size-guidance-style';
      style.textContent = `
        [data-spazioil-size-field="true"]{width:100%!important;max-width:100%;flex-basis:100%}
        select[data-spazioil-size-select="true"]{width:100%;max-width:100%;min-width:0;padding-right:40px;text-overflow:ellipsis;font-variant-numeric:tabular-nums}
        .spazioil-size-note{margin:10px 0 2px;color:#6d5e52;font-size:13px;line-height:1.55}
        .spazioil-size-note__label{display:block;margin-bottom:4px;color:#604631;font-weight:600;font-size:14px}
        .spazioil-size-note>span{display:block;max-width:58ch}
        @media(max-width:749px){select[data-spazioil-size-select="true"]{font-size:13px;padding-left:12px;padding-right:32px}.spazioil-size-note{font-size:12px}}
      `;
      document.head.append(style);
    }
    scan(document);
    new MutationObserver(mutations => {
      for (const mutation of mutations) for (const node of mutation.addedNodes) {
        if (node instanceof Element && (node.matches('select') || node.querySelector('select'))) scan(node);
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', event => scan(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();
